/**
 * Constants for Contact Us functionality
 * Centralizes all magic strings, regex patterns, and configuration values
 */

/**
 * Form validation patterns
 * Note: Email regex is designed to prevent ReDoS (Regular Expression Denial of Service)
 * by using specific character classes instead of negated ones with unbounded quantifiers.
 */
export const VALIDATION_PATTERNS = {
  /**
   * Email validation pattern - ReDoS-safe implementation
   * Matches: local@domain.tld
   * Local part: alphanumeric, dots, hyphens, plus signs, underscores
   * Domain: alphanumeric, dots, hyphens
   * TLD: at least 2 alphabetic characters
   * Performance: O(n) complexity, no backtracking vulnerabilities
   */
  EMAIL: /^[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
  PHONE_AREA_CODE: /^\d{3}$/,
  PHONE_NUMBER: /^\d{3}[-. ]?\d{4}$/,
  PHONE_NUMBER_DIGITS_ONLY: /^\d{7}$/,
  NUMERIC_ONLY: /\D/g,
  PHONE_SEPARATOR: /[-. ]/g,
} as const;

/** Field length constraints */
export const FIELD_LIMITS = {
  /**
   * Maximum email length to prevent ReDoS attacks
   * RFC 5321 specifies 254 characters for email addresses
   * We validate length before applying regex pattern
   */
  EMAIL_MAX_LENGTH: 254,
  PHONE_AREA_CODE_LENGTH: 3,
  PHONE_NUMBER_LENGTH: 7,
  PHONE_NUMBER_MAX_INPUT_LENGTH: 8,
  COMMENTS_MAX_LENGTH: 2048,
} as const;

/** Timeout durations (in milliseconds) */
export const TIMEOUTS = {
  MESSAGE_AUTO_DISMISS: 5000,
  SCROLL_DELAY: 100,
} as const;

/** Error messages */
export const ERROR_MESSAGES = {
  REGION_REQUIRED: 'Region is required. Please select a region.',
  CENTER_REQUIRED: 'Center is required. First, select region then center.',
  TOPIC_REQUIRED: 'Topic is required. First, select center then topic.',
  TEAM_NAME_REQUIRED: 'Team Name or UBT ID is required.',
  EMAIL_REQUIRED: 'Email is required.',
  EMAIL_INVALID: 'Please enter a valid email address.',
  PHONE_AREA_CODE_INVALID: 'Phone Area Code must be exactly 3 digits.',
  PHONE_NUMBER_INVALID: 'Invalid phone number format. Please use XXX-XXXX format.',
  COMMENTS_REQUIRED: 'Comments are required.',
  LOAD_INITIAL_DATA_FAILED: 'Failed to load contact information. Please try again.',
  LOAD_CENTERS_FAILED: 'Failed to load centers. Please try again.',
  LOAD_TOPICS_FAILED: 'Failed to load topics. Please try again.',
  SEND_MESSAGE_FAILED: 'Failed to send your message. Please try again.',
} as const;

/** Success messages */
export const SUCCESS_MESSAGES = {
  MESSAGE_SENT: 'Message successfully sent.',
} as const;

/** Helper text */
export const HELPER_TEXT = {
  READ_ONLY_HR: 'Read only; this value is assigned automatically by HR',
  REQUIRED_FIELD: 'Required Field',
} as const;

/** Field labels */
export const FIELD_LABELS = {
  NUID: 'National User ID (NUID)',
  FIRST_NAME: 'First Name',
  LAST_NAME: 'Last Name',
  EMAIL: 'Email',
  REGION: 'Region',
  CENTER: 'Center',
  TOPIC: 'Topic',
  TEAM_NAME_UBT_ID: 'Team Name or UBT ID',
  PHONE_AREA_CODE: 'Phone Area Code',
  PHONE_NUMBER: 'Phone Number',
  COMMENTS: 'Comments',
} as const;

/** Field placeholders */
export const FIELD_PLACEHOLDERS = {
  EMAIL: 'john.doe@kp.org',
  TEAM_NAME_UBT_ID: 'Team Name or UBT ID',
  PHONE_AREA_CODE: 'Phone Area Code',
  PHONE_NUMBER: 'Phone Number',
  COMMENTS: 'Enter your comments here',
  SELECT: 'Select',
} as const;

/** Button text */
export const BUTTON_TEXT = {
  CLEAR: 'Clear',
  SEND: 'Send',
  SENDING: 'Sending...',
} as const;

/** Page content */
export const PAGE_CONTENT = {
  TITLE: 'Contact Us',
  DESCRIPTION: 'Report an error, suggest an idea or ask questions about Unit Based Teams and UBT Tracker to your UBT Administrator.',
  BREADCRUMB_PREVIOUS: 'UBT Home',
} as const;

/** Default dropdown option */
export const DEFAULT_DROPDOWN_OPTION = {
  id: '',
  label: FIELD_PLACEHOLDERS.SELECT,
  value: '',
} as const;

/** Required field marker */
export const REQUIRED_MARKER = '*';

/** Load value types for API calls */
export const LOAD_VALUE_TYPES = {
  REGION: 'REGION',
  CENTER: 'CENTER',
  FEEDBACK_TYPE: 'FEEDBACK_TYPE',
} as const;
